import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { KeyRound, Loader2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/api/auth/reset-token/${token}`)
      .then((res) => setEmail(res.data.email))
      .catch((err) => setTokenError(formatApiError(err.response?.data?.detail) || 'This reset link is not valid.'));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setBusy(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      toast.success('Password updated', { description: 'You can now sign in with your new password.' });
      navigate('/login', { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Could not reset password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <Header variant="solid" />
      <div className="pt-32 md:pt-40 pb-24 container mx-auto">
        <div className="max-w-md mx-auto rounded-3xl bg-card border border-border/70 shadow-card p-8 sm:p-10">
          <div className="h-12 w-12 rounded-2xl gradient-leaf grid place-items-center text-primary-foreground shadow-card">
            <KeyRound className="h-5 w-5" />
          </div>
          {tokenError ? (
            <div data-testid="reset-token-error">
              <h1 className="font-display text-3xl font-semibold text-primary mt-5">Link issue</h1>
              <p className="text-sm text-muted-foreground mt-2">{tokenError}</p>
              <Button asChild variant="outline" className="w-full rounded-full mt-6">
                <Link to="/forgot-password">Request a new link</Link>
              </Button>
            </div>
          ) : !email ? (
            <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-leaf" /></div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-semibold text-primary mt-5">Choose a new password</h1>
              <p className="text-sm text-muted-foreground mt-1.5">for <strong className="text-primary">{email}</strong></p>
              <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="reset-form">
                <div>
                  <Label htmlFor="rp-pass" className="text-primary mb-1.5">New password (min. 8 characters)</Label>
                  <Input id="rp-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    className="h-11 rounded-xl" data-testid="reset-password-input" />
                </div>
                <div>
                  <Label htmlFor="rp-confirm" className="text-primary mb-1.5">Confirm new password</Label>
                  <Input id="rp-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="h-11 rounded-xl" data-testid="reset-confirm-input" />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="reset-form-error">{error}</p>}
                <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="reset-submit-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}
